import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { canUse, FEATURES, type Feature } from "../../domain/access/features";
import type { CanonicalCase, CaseDraft } from "../../domain/case/canonical-case";
import {
  buildCanonicalCase,
  draftFromCase,
  makeCaseTombstone,
  updateCanonicalCase,
} from "../../domain/case/case-adapter";
import { mergeFreshestCaseSnapshots } from "../../domain/case/case-merge";
import { nextIsoTimestamp } from "../../domain/case/timestamps";
import {
  createCase as createCloudCase,
  createCasesInBatches,
  fetchAllCases,
  observeCaseChanges,
  PartialCaseWriteError,
  PartialCaseUpdateError,
  type ExpectedCaseWrite,
  updateCase as updateCloudCase,
  updateCasesWithConflictChecks,
} from "../../infrastructure/firebase/case-repository";
import {
  observeSyncMetadataChanges,
  readSyncChangeMarker,
  reconcileSyncCaseCount,
  type SyncChangeMarker,
} from "../../infrastructure/firebase/sync-metadata";
import {
  recordCaseAddition,
  recordFirstSyncCompleted,
} from "../../infrastructure/firebase/telemetry";
import { getDeviceId } from "../../infrastructure/local/device-identity";
import {
  putCachedCases,
  readCachedCases,
  replaceCachedCases,
  studioDb,
  workspaceScope,
  type UndoJournalRecord,
} from "../../infrastructure/local/studio-db";
import { useAuth } from "./AuthProvider";
import { createDemoCases } from "../../demo/demo-data";

interface WorkspaceNotice {
  id: string;
  tone: "success" | "warning" | "error" | "info";
  message: string;
  actionLabel?: string;
}

export interface CaseCreateInput {
  /** Stable client-generated ID, reused if a staged list save is retried. */
  id: string;
  draft: CaseDraft;
}

interface WorkspaceContextValue {
  cases: CanonicalCase[];
  activeCases: CanonicalCase[];
  loading: boolean;
  progress: number;
  error: string | null;
  notice: WorkspaceNotice | null;
  can: (feature: Feature) => boolean;
  refresh: () => Promise<void>;
  addCase: (draft: CaseDraft, addAnother?: boolean) => Promise<CanonicalCase>;
  addCases: (inputs: readonly CaseCreateInput[]) => Promise<CanonicalCase[]>;
  saveCase: (existing: CanonicalCase, draft: CaseDraft) => Promise<CanonicalCase>;
  duplicateCase: (existing: CanonicalCase) => Promise<CanonicalCase>;
  archiveCases: (ids: string[], archived: boolean) => Promise<void>;
  deleteCases: (ids: string[]) => Promise<void>;
  bulkPatchCases: (ids: string[], patch: Partial<CaseDraft>) => Promise<void>;
  commitImportedCases: (
    values: CanonicalCase[],
    onProgress?: (committed: number, total: number) => void,
  ) => Promise<string[]>;
  undoLast: () => Promise<void>;
  clearNotice: () => void;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

function active(values: CanonicalCase[]): CanonicalCase[] {
  return values.filter((item) => !item.isDeleted);
}

function sortCases(values: CanonicalCase[]): CanonicalCase[] {
  return [...values].sort((left, right) =>
    right.date.localeCompare(left.date) || right.updatedAt.localeCompare(left.updatedAt));
}

function replaceValues(
  values: CanonicalCase[],
  replacements: CanonicalCase[],
): CanonicalCase[] {
  const byId = new Map(values.map((item) => [item.id, item]));
  replacements.forEach((item) => byId.set(item.id, item));
  return sortCases([...byId.values()]);
}

export function WorkspaceProvider({
  children,
  demoMode = false,
}: {
  children: ReactNode;
  demoMode?: boolean;
}) {
  if (demoMode) return <DemoWorkspaceProvider>{children}</DemoWorkspaceProvider>;
  return <CloudWorkspaceProvider>{children}</CloudWorkspaceProvider>;
}

function CloudWorkspaceProvider({ children }: { children: ReactNode }) {
  const { user, entitlement, loading: authLoading } = useAuth();
  const scope = workspaceScope(user?.uid);
  const [cases, setCases] = useState<CanonicalCase[]>([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<WorkspaceNotice | null>(null);
  const [casesScope, setCasesScope] = useState(scope);
  const loadGeneration = useRef(0);
  const remoteChangeGeneration = useRef(0);
  const casesReference = useRef<CanonicalCase[]>([]);
  const cacheWriteQueue = useRef<Promise<void>>(Promise.resolve());
  const authScopeReference = useRef(scope);
  const caseScopeReference = useRef(scope);

  useLayoutEffect(() => {
    // Account changes are a synchronous presentation boundary. Invalidate any
    // in-flight work before paint and remove account-owned status/actions so a
    // notice such as “Undo” can never survive into the next account.
    authScopeReference.current = scope;
    loadGeneration.current += 1;
    setProgress(0);
    setError(null);
    setNotice(null);
  }, [scope]);

  const commitCaseState = useCallback((next: CanonicalCase[]) => {
    casesReference.current = next;
    setCases(next);
  }, []);

  const mergeCaseState = useCallback((changed: CanonicalCase[]): CanonicalCase[] => {
    remoteChangeGeneration.current += 1;
    const next = replaceValues(casesReference.current, changed);
    commitCaseState(next);
    return next;
  }, [commitCaseState]);

  const operationIsCurrent = useCallback((uid: string): boolean => {
    const operationScope = workspaceScope(uid);
    return authScopeReference.current === operationScope
      && caseScopeReference.current === operationScope;
  }, []);

  const mergeCaseStateForUser = useCallback((uid: string, changed: CanonicalCase[]): boolean => {
    if (!operationIsCurrent(uid)) return false;
    mergeCaseState(changed);
    return true;
  }, [mergeCaseState, operationIsCurrent]);

  const enqueueCacheWrite = useCallback(async (
    operation: () => Promise<void>,
    warning: string,
  ): Promise<void> => {
    const queued = cacheWriteQueue.current.catch(() => undefined).then(operation);
    cacheWriteQueue.current = queued.catch((reason) => {
      console.warn(warning, reason);
    });
    await cacheWriteQueue.current;
  }, []);

  const cacheCases = useCallback((values: CanonicalCase[]) => enqueueCacheWrite(
    () => putCachedCases(scope, values),
    "Studio could not update its local case cache after a cloud commit.",
  ), [enqueueCacheWrite, scope]);

  const replaceCaseCache = useCallback((values: CanonicalCase[]) => enqueueCacheWrite(
    () => replaceCachedCases(scope, values),
    "Studio could not replace its local case cache.",
  ), [enqueueCacheWrite, scope]);

  const can = useCallback((feature: Feature) => canUse(feature, {
    authenticated: Boolean(user),
    entitlement,
  }), [entitlement, user]);

  const showNotice = useCallback((next: Omit<WorkspaceNotice, "id">) => {
    setNotice({ ...next, id: crypto.randomUUID() });
  }, []);

  const refresh = useCallback(async () => {
    const generation = ++loadGeneration.current;
    setError(null);
    if (caseScopeReference.current !== scope) {
      caseScopeReference.current = scope;
      setCasesScope(scope);
      remoteChangeGeneration.current = 0;
      commitCaseState([]);
    }
    const changeGenerationAtStart = remoteChangeGeneration.current;
    if (!user || !can(FEATURES.cloudRead)) {
      commitCaseState([]);
      setProgress(0);
      setLoading(false);
      return;
    }

    setLoading(true);
    let cached: CanonicalCase[] = [];
    try {
      cached = await readCachedCases(scope);
    } catch (reason) {
      console.warn("Studio could not read its local case cache.", reason);
    }
    if (generation !== loadGeneration.current) return;
    if (cached.length > 0 && casesReference.current.length === 0) {
      commitCaseState(sortCases(cached));
    }

    try {
      // A marker can change between the pre-fetch read and the initial listener
      // snapshots. Retry the full read once when the guarded count check detects
      // that window, rather than waiting for an unrelated later mutation.
      for (let attempt = 0; attempt < 2; attempt += 1) {
        let syncMarker: SyncChangeMarker | null = null;
        try {
          syncMarker = await readSyncChangeMarker(user.uid);
        } catch (reason) {
          console.warn("Studio could not read the sync marker before refreshing cases.", reason);
        }
        if (generation !== loadGeneration.current) return;

        const cloud = await fetchAllCases(user.uid, {
          onProgress: ({ loaded }) => {
            if (generation === loadGeneration.current && authScopeReference.current === scope) {
              setProgress(loaded);
            }
          },
        });
        if (generation !== loadGeneration.current) return;
        const next = sortCases(
          remoteChangeGeneration.current === changeGenerationAtStart
            ? cloud
            : mergeFreshestCaseSnapshots(cloud, casesReference.current),
        );
        commitCaseState(next);
        await replaceCaseCache(next);

        let markerChanged = false;
        if (syncMarker && generation === loadGeneration.current) {
          try {
            markerChanged = await reconcileSyncCaseCount(
              user.uid,
              syncMarker,
              cloud.filter((item) => !item.isDeleted).length,
            ) === "marker-changed";
          } catch (reason) {
            console.warn("Studio could not reconcile the advisory cloud case count.", reason);
          }
        }
        if (!markerChanged || attempt > 0) break;
      }
      void recordFirstSyncCompleted();
    } catch (reason) {
      if (generation !== loadGeneration.current) return;
      const message = reason instanceof Error ? reason.message : "Cases could not be loaded.";
      setError(message);
    } finally {
      if (generation === loadGeneration.current) setLoading(false);
    }
  }, [can, commitCaseState, replaceCaseCache, scope, user]);

  useEffect(() => {
    if (!authLoading) void refresh();
  }, [authLoading, refresh]);

  useEffect(() => {
    if (authLoading || !user || !can(FEATURES.cloudSync)) return undefined;
    const since = new Date(Date.now() - 5 * 60 * 1_000).toISOString();
    return observeCaseChanges(user.uid, since, (changed) => {
      if (!mergeCaseStateForUser(user.uid, changed)) return;
      void cacheCases(changed);
      if (changed.length >= 2_000) void refresh();
    }, (reason) => {
      if (operationIsCurrent(user.uid)) setError(reason.message);
    });
  }, [authLoading, cacheCases, can, mergeCaseStateForUser, operationIsCurrent, refresh, user]);

  useEffect(() => {
    if (authLoading || !user || !can(FEATURES.cloudSync)) return undefined;
    let refreshTimer: ReturnType<typeof setTimeout> | undefined;
    const unsubscribe = observeSyncMetadataChanges(user.uid, () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      refreshTimer = setTimeout(() => { void refresh(); }, 150);
    }, (reason) => {
      if (operationIsCurrent(user.uid)) setError(reason.message);
    });
    return () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      unsubscribe();
    };
  }, [authLoading, can, operationIsCurrent, refresh, user]);

  const requireCloudMutation = useCallback((feature: Feature): string => {
    if (!user) throw new Error("Sign in before changing cloud cases.");
    if (!can(feature)) throw new Error("LogBookAnaes Pro is required for this action.");
    if (!operationIsCurrent(user.uid)) {
      throw new Error("Your logbook is still switching accounts. Try again in a moment.");
    }
    return user.uid;
  }, [can, operationIsCurrent, user]);

  const recordUndo = useCallback(async (
    kind: UndoJournalRecord["kind"],
    label: string,
    before: CanonicalCase[],
    affectedCaseIds: string[],
  ) => {
    const createdAt = new Date();
    const operation: UndoJournalRecord = {
      id: crypto.randomUUID(),
      ownerScope: scope,
      kind,
      label,
      createdAt: createdAt.toISOString(),
      expiresAt: new Date(createdAt.getTime() + 7 * 24 * 60 * 60 * 1_000).toISOString(),
      before,
      affectedCaseIds,
    };
    try {
      await studioDb.undoJournal.add(operation);
      if (authScopeReference.current === scope && caseScopeReference.current === scope) {
        showNotice({ tone: "success", message: label, actionLabel: "Undo" });
      }
    } catch (reason) {
      console.warn("Studio could not record local undo history after a cloud commit.", reason);
      if (authScopeReference.current === scope && caseScopeReference.current === scope) {
        showNotice({ tone: "warning", message: `${label} Undo is unavailable in this browser.` });
      }
    }
  }, [scope, showNotice]);

  const writeCheckedCases = useCallback(async (uid: string, writes: ExpectedCaseWrite[]) => {
    try {
      await updateCasesWithConflictChecks(uid, writes);
    } catch (reason) {
      if (reason instanceof PartialCaseUpdateError && operationIsCurrent(uid)) await refresh();
      throw reason;
    }
  }, [operationIsCurrent, refresh]);

  const addCase = useCallback(async (draft: CaseDraft): Promise<CanonicalCase> => {
    const uid = requireCloudMutation(FEATURES.caseCreate);
    const previousCaseCount = active(casesReference.current).length;
    const value = buildCanonicalCase(draft, { deviceId: getDeviceId() });
    const persisted = await createCloudCase(uid, value);
    mergeCaseStateForUser(uid, [persisted]);
    await cacheCases([persisted]);
    if (operationIsCurrent(uid)) {
      showNotice({ tone: "success", message: "Case saved." });
      void recordCaseAddition(previousCaseCount, 1);
    }
    return persisted;
  }, [cacheCases, mergeCaseStateForUser, operationIsCurrent, requireCloudMutation, showNotice]);

  const addCases = useCallback(async (
    inputs: readonly CaseCreateInput[],
  ): Promise<CanonicalCase[]> => {
    if (inputs.length === 0) return [];
    if (inputs.length > 400) {
      throw new Error("Add List supports up to 400 cases at once. Use Import for larger datasets.");
    }
    const ids = inputs.map((input) => input.id.trim());
    if (ids.some((id) => !id) || new Set(ids).size !== ids.length) {
      throw new Error("Every staged case must have a unique ID before it can be saved.");
    }
    if (inputs.some(({ draft }) => !draft.date.trim() || !draft.procedure.trim())) {
      throw new Error("Every staged case needs a date and procedure before saving.");
    }

    const uid = requireCloudMutation(FEATURES.caseCreate);
    const previousCaseCount = active(casesReference.current).length;
    const now = new Date();
    const deviceId = getDeviceId();
    const values = inputs.map(({ draft }, index) => buildCanonicalCase(draft, {
      id: ids[index]!,
      now,
      deviceId,
      sourceSystem: "LogBookAnaes Studio Add List",
    }));

    // Sync v3 accepts 25 mutations per request. Its receipts make a transport
    // retry safe, while Studio retains the canonical response for later edits.
    await createCasesInBatches(uid, values);
    mergeCaseStateForUser(uid, values);
    await cacheCases(values);
    await recordUndo(
      "create-list",
      `${values.length} case${values.length === 1 ? "" : "s"} saved from Add List.`,
      [],
      values.map((value) => value.id),
    );
    if (operationIsCurrent(uid)) {
      void recordCaseAddition(previousCaseCount, values.length);
    }
    return values;
  }, [cacheCases, mergeCaseStateForUser, operationIsCurrent, recordUndo, requireCloudMutation]);

  const saveCase = useCallback(async (
    existing: CanonicalCase,
    draft: CaseDraft,
  ): Promise<CanonicalCase> => {
    const uid = requireCloudMutation(FEATURES.caseEdit);
    const current = casesReference.current.find((item) => item.id === existing.id);
    if (!current || current.updatedAt !== existing.updatedAt) {
      throw new Error("This case is no longer in the current logbook. Reopen it before saving.");
    }
    const updated = updateCanonicalCase(current, draft);
    const persisted = await updateCloudCase(uid, updated, current.updatedAt);
    mergeCaseStateForUser(uid, [persisted]);
    await cacheCases([persisted]);
    await recordUndo("edit", "Case updated.", [current], [current.id]);
    return persisted;
  }, [cacheCases, mergeCaseStateForUser, recordUndo, requireCloudMutation]);

  const duplicateCase = useCallback(async (existing: CanonicalCase): Promise<CanonicalCase> => {
    const uid = requireCloudMutation(FEATURES.caseCreate);
    const previousCaseCount = active(casesReference.current).length;
    const current = casesReference.current.find((item) => item.id === existing.id);
    if (!current || current.updatedAt !== existing.updatedAt) {
      throw new Error("This case is no longer in the current logbook. Reopen it before duplicating.");
    }
    const duplicated = buildCanonicalCase(draftFromCase(current), {
      deviceId: getDeviceId(),
      sourceSystem: "LogBookAnaes Studio",
    });
    const persisted = await createCloudCase(uid, duplicated);
    mergeCaseStateForUser(uid, [persisted]);
    await cacheCases([persisted]);
    if (operationIsCurrent(uid)) {
      showNotice({ tone: "success", message: "Case duplicated." });
      void recordCaseAddition(previousCaseCount, 1);
    }
    return persisted;
  }, [cacheCases, mergeCaseStateForUser, operationIsCurrent, requireCloudMutation, showNotice]);

  const archiveCases = useCallback(async (ids: string[], archived: boolean) => {
    const uid = requireCloudMutation(FEATURES.caseArchive);
    const selected = casesReference.current.filter((item) => ids.includes(item.id));
    if (!selected.length) throw new Error("No current cases were selected.");
    const now = new Date();
    const updated = selected.map((item): CanonicalCase => {
      const timestamp = nextIsoTimestamp(now, item.updatedAt, item.lastUpdated);
      return {
        ...item,
        updatedAt: timestamp,
        lastUpdated: timestamp,
        studio: { ...item.studio, archivedAt: archived ? timestamp : null },
      };
    });
    await writeCheckedCases(uid, updated.map((value, index) => ({
      value,
      expectedUpdatedAt: selected[index]?.updatedAt ?? "",
    })));
    mergeCaseStateForUser(uid, updated);
    await cacheCases(updated);
    await recordUndo("archive", `${updated.length} case${updated.length === 1 ? "" : "s"} ${archived ? "archived" : "restored"}.`, selected, ids);
  }, [cacheCases, mergeCaseStateForUser, recordUndo, requireCloudMutation, writeCheckedCases]);

  const deleteCases = useCallback(async (ids: string[]) => {
    const uid = requireCloudMutation(FEATURES.caseDelete);
    const selected = casesReference.current.filter((item) => ids.includes(item.id));
    if (!selected.length) throw new Error("No current cases were selected.");
    const tombstones = selected.map((item) => makeCaseTombstone(item));
    await writeCheckedCases(uid, tombstones.map((value, index) => ({
      value,
      expectedUpdatedAt: selected[index]?.updatedAt ?? "",
    })));
    mergeCaseStateForUser(uid, tombstones);
    await cacheCases(tombstones);
    await recordUndo("delete", `${selected.length} case${selected.length === 1 ? "" : "s"} moved to Recently Deleted.`, selected, ids);
  }, [cacheCases, mergeCaseStateForUser, recordUndo, requireCloudMutation, writeCheckedCases]);

  const bulkPatchCases = useCallback(async (ids: string[], patch: Partial<CaseDraft>) => {
    const uid = requireCloudMutation(FEATURES.bulkEdit);
    const selected = casesReference.current.filter((item) => ids.includes(item.id));
    if (!selected.length) throw new Error("No current cases were selected.");
    const updated = selected.map((item) => {
      const draft = { ...draftFromCase(item), ...patch };
      return updateCanonicalCase(item, draft);
    });
    await writeCheckedCases(uid, updated.map((value, index) => ({
      value,
      expectedUpdatedAt: selected[index]?.updatedAt ?? "",
    })));
    mergeCaseStateForUser(uid, updated);
    await cacheCases(updated);
    await recordUndo("bulk-edit", `${updated.length} cases updated.`, selected, ids);
  }, [cacheCases, mergeCaseStateForUser, recordUndo, requireCloudMutation, writeCheckedCases]);

  const commitImportedCases = useCallback(async (
    values: CanonicalCase[],
    onProgress?: (committed: number, total: number) => void,
  ) => {
    const uid = requireCloudMutation(FEATURES.importCommit);
    let committed: string[];
    try {
      committed = await createCasesInBatches(uid, values, ({ committed: count, total }) => {
        if (operationIsCurrent(uid)) onProgress?.(count, total);
      });
    } catch (reason) {
      if (reason instanceof PartialCaseWriteError && reason.committedCaseIds.length > 0) {
        const ids = new Set(reason.committedCaseIds);
        const partial = values.filter((item) => ids.has(item.id));
        mergeCaseStateForUser(uid, partial);
        await cacheCases(partial);
        await recordUndo("import", `${partial.length} partially imported cases can be undone.`, [], reason.committedCaseIds);
      }
      throw reason;
    }
    mergeCaseStateForUser(uid, values);
    await cacheCases(values);
    await recordUndo("import", `${committed.length} imported cases committed.`, [], committed);
    return committed;
  }, [cacheCases, mergeCaseStateForUser, operationIsCurrent, recordUndo, requireCloudMutation]);

  const undoLast = useCallback(async () => {
    const uid = requireCloudMutation(FEATURES.caseEdit);
    const latest = await studioDb.undoJournal
      .where("ownerScope").equals(scope)
      .reverse().sortBy("createdAt")
      .then((items) => items[0]);
    if (!operationIsCurrent(uid)) {
      throw new Error("Your logbook changed before Undo could start. Try again in the current account.");
    }
    if (!latest) {
      showNotice({ tone: "info", message: "Nothing to undo." });
      return;
    }

    const currentById = new Map(casesReference.current.map((item) => [item.id, item]));
    let restored: CanonicalCase[];
    if (latest.before.length > 0) {
      const now = new Date();
      restored = latest.before.map((item) => {
        const current = currentById.get(item.id);
        const timestamp = nextIsoTimestamp(
          now,
          item.updatedAt,
          item.lastUpdated,
          current?.updatedAt,
          current?.lastUpdated,
        );
        return {
          ...item,
          updatedAt: timestamp,
          lastUpdated: timestamp,
          isDeleted: false,
          deletedAt: null,
        };
      });
    } else {
      restored = latest.affectedCaseIds.flatMap((id) => {
        const current = currentById.get(id);
        return current ? [makeCaseTombstone(current)] : [];
      });
    }
    await writeCheckedCases(uid, restored.map((value) => ({
      value,
      expectedUpdatedAt: currentById.get(value.id)?.updatedAt ?? value.updatedAt,
    })));
    mergeCaseStateForUser(uid, restored);
    await cacheCases(restored);
    try {
      await studioDb.undoJournal.delete(latest.id);
    } catch (reason) {
      console.warn("Studio could not remove completed local undo history.", reason);
    }
    if (operationIsCurrent(uid)) {
      showNotice({ tone: "success", message: "Last change undone." });
    }
  }, [cacheCases, mergeCaseStateForUser, operationIsCurrent, requireCloudMutation, scope, showNotice, writeCheckedCases]);

  const value = useMemo<WorkspaceContextValue>(() => {
    // React renders once before the refresh effect runs after an account switch.
    // Never expose rows owned by the previous scope during that render.
    const exposedCases = casesScope === scope ? cases : [];
    return {
      cases: exposedCases,
      activeCases: active(exposedCases),
      loading,
      progress,
      error,
      notice,
      can,
      refresh,
      addCase,
      addCases,
      saveCase,
      duplicateCase,
      archiveCases,
      deleteCases,
      bulkPatchCases,
      commitImportedCases,
      undoLast,
      clearNotice: () => setNotice(null),
    };
  }, [
    addCase,
    addCases,
    archiveCases,
    bulkPatchCases,
    can,
    cases,
    casesScope,
    commitImportedCases,
    deleteCases,
    duplicateCase,
    error,
    loading,
    notice,
    progress,
    refresh,
    saveCase,
    scope,
    undoLast,
  ]);

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

const demoFeatures = new Set<Feature>([
  FEATURES.dashboardLocal,
  FEATURES.cloudRead,
  FEATURES.previewReports,
  FEATURES.basicReports,
  FEATURES.advancedReports,
  FEATURES.localExport,
]);

function DemoWorkspaceProvider({ children }: { children: ReactNode }) {
  const cases = useMemo(() => createDemoCases(), []);
  const readOnly = useCallback((): Promise<never> => Promise.reject(
    new Error("The sample workspace is read-only. Nothing here can be saved."),
  ), []);
  const value = useMemo<WorkspaceContextValue>(() => ({
    cases,
    activeCases: cases,
    loading: false,
    progress: cases.length,
    error: null,
    notice: null,
    can: (feature) => demoFeatures.has(feature),
    refresh: () => Promise.resolve(),
    addCase: readOnly,
    addCases: readOnly,
    saveCase: readOnly,
    duplicateCase: readOnly,
    archiveCases: readOnly,
    deleteCases: readOnly,
    bulkPatchCases: readOnly,
    commitImportedCases: readOnly,
    undoLast: readOnly,
    clearNotice: () => undefined,
  }), [cases, readOnly]);

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace(): WorkspaceContextValue {
  const value = useContext(WorkspaceContext);
  if (!value) throw new Error("useWorkspace must be used inside WorkspaceProvider.");
  return value;
}
